import Dexie, { type Table } from 'dexie'
import { DEFAULT_SETTINGS } from '../app/constants'
import type { AppSettings, DraftRecord, ParsedDocument, ReadingProgress, RecentRecord } from '../app/types'

interface SettingRecord {
  key: string
  value: AppSettings
}

class LightDocDatabase extends Dexie {
  recents!: Table<RecentRecord, string>
  drafts!: Table<DraftRecord, string>
  settings!: Table<SettingRecord, string>
  readingProgress!: Table<ReadingProgress, string>

  constructor() {
    super('qingyuedang-local-db')
    this.version(1).stores({
      recents: 'id, kind, openedAt, name, favorite',
      drafts: 'id, kind, updatedAt, name',
      settings: 'key',
    })
    this.version(2).stores({
      recents: 'id, kind, openedAt, name, favorite',
      drafts: 'id, kind, updatedAt, name',
      settings: 'key',
      readingProgress: 'id, updatedAt',
    })
  }
}

export const db = new LightDocDatabase()

export async function listRecents(): Promise<RecentRecord[]> {
  const records = await db.recents.toArray()
  return records
    .sort((left, right) => Number(Boolean(right.favorite)) - Number(Boolean(left.favorite)) || right.openedAt - left.openedAt)
    .slice(0, 20)
}

export async function saveRecent(document: ParsedDocument): Promise<void> {
  const existing = await db.recents.get(document.id)
  const record: RecentRecord = {
    id: document.id,
    name: document.meta.name,
    kind: document.kind,
    size: document.meta.size,
    mime: document.meta.mime,
    fingerprint: document.meta.fingerprint,
    openedAt: Date.now(),
    draftUpdatedAt: document.meta.draftUpdatedAt,
    favorite: document.meta.favorite ?? existing?.favorite,
    warnings: document.warnings,
  }

  await db.recents.put(JSON.parse(JSON.stringify(record)) as RecentRecord)
}

export async function saveDraft(document: ParsedDocument): Promise<void> {
  const updatedAt = Date.now()
  const plainDocument = JSON.parse(JSON.stringify({
    ...document,
    meta: {
      ...document.meta,
      draftUpdatedAt: updatedAt,
    },
  })) as ParsedDocument

  await db.drafts.put({
    id: document.id,
    kind: document.kind,
    name: document.meta.name,
    updatedAt,
    document: plainDocument,
  })

  await db.recents.update(document.id, { draftUpdatedAt: updatedAt })
}

export async function getDraft(id: string): Promise<DraftRecord | undefined> {
  return db.drafts.get(id)
}

export async function setRecentFavorite(id: string, favorite: boolean): Promise<void> {
  await db.transaction('rw', db.recents, db.drafts, async () => {
    await db.recents.update(id, { favorite })
    const draft = await db.drafts.get(id)
    if (draft) {
      await db.drafts.put({
        ...draft,
        document: {
          ...draft.document,
          meta: {
            ...draft.document.meta,
            favorite,
          },
        },
      })
    }
  })
}

export async function getReadingProgress(id: string): Promise<ReadingProgress | undefined> {
  return db.readingProgress.get(id)
}

export async function saveReadingProgress(progress: ReadingProgress): Promise<void> {
  await db.readingProgress.put(progress)
}

export async function removeLocalRecord(id: string): Promise<void> {
  await db.transaction('rw', db.recents, db.drafts, db.readingProgress, async () => {
    await db.recents.delete(id)
    await db.drafts.delete(id)
    await db.readingProgress.delete(id)
  })
}

export async function clearLocalData(): Promise<void> {
  await db.transaction('rw', db.recents, db.drafts, db.readingProgress, async () => {
    await db.recents.clear()
    await db.drafts.clear()
    await db.readingProgress.clear()
  })
}

export async function loadSettings(): Promise<AppSettings> {
  const record = await db.settings.get('app')
  return record?.value ?? DEFAULT_SETTINGS
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  const value = JSON.parse(JSON.stringify(settings)) as AppSettings
  await db.settings.put({ key: 'app', value })
}
